import { fn, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { get } from '@ember/object';
import { action } from '@ember/object';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ComponentLike } from '@glint/template';
import { compareAsc, compareDesc } from 'date-fns';
import { eq, gt } from 'ember-truth-helpers';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import clickOutside from 'garaje/modifiers/click-outside';

interface Row {
  id: string;
}

interface ComponentOptions {
  template: string | ComponentLike;
}

export interface ColumnHeader {
  key: string; // Key of attribute of record to display. Can be a nested key. E.g., for `Entry`, key can be `employee.name`
  label: string;
  sortable: boolean;
  componentOptions?: ComponentOptions;
  class?: string; // apply styles to specific column
}

interface TableAction<TRow extends Row> {
  label: string;
  icon?: string; // icon path or class
  iconClass?: string; // for icon styling
  onAction: (row: TRow) => void;
  className?: string; // for text styling
  disabled?: (row: TRow) => boolean;
}

// generic type TRow just enforces that each row record will be an object with the same type
interface TableComponentArgs<TRow extends Row> {
  columnHeaders: Array<ColumnHeader>;
  data: Dataset<TRow>;
  actions?: TableAction<TRow>[];
  headerClass?: string; // apply styles to all headers
  disableActionForRow?: (row: TRow) => boolean;
  onRowClick?: (row: TRow) => void; // when row is clicked
  onRowSelect?: (row: TRow) => void; // when action menu is clicked
  onSortToggle?: (columnKey: string, sortDirection: SortDirection) => void;
  onClearRowSelection?: () => void;
  onSelectAll?: (isSelected: boolean) => void;
}

interface TableComponentSignature<TRow extends Row> {
  Args: TableComponentArgs<TRow>;
  Blocks: {
    default: [TableComponent<TRow>];
  };
  Element: HTMLDivElement;
}

type SortDirection = 'asc' | 'desc';
type Dataset<TRow extends Row> = Array<TRow>;

export default class TableComponent<TRow extends Row> extends Component<TableComponentSignature<TRow>> {
  @tracked sortBy: string | null = null;
  @tracked sortDirection: SortDirection = 'asc';
  @tracked activeActionMenuId: string | null = null;
  @tracked actionMenuElement: HTMLElement | null = null;

  get lastActionIndex(): number {
    return (this.args.actions?.length ?? 1) - 1;
  }

  get sortedData(): TRow[] {
    return this.sortRecordsByKey;
  }

  get showActionMenu(): (record: TRow) => boolean {
    return (record) => this.activeActionMenuId === record.id;
  }

  get hasActionMenu(): (record: TRow) => boolean {
    return (record) => {
      if (this.args.disableActionForRow?.(record)) {
        return false;
      }
      return Array.isArray(this.args.actions) && this.args.actions.length > 0;
    };
  }

  compareDateTimeFields(firstDate: Date, secondDate: Date, sortingDirection: SortDirection): number {
    if (sortingDirection === 'asc') {
      return compareAsc(firstDate, secondDate);
    }
    return compareDesc(firstDate, secondDate);
  }
  compareFields(firstFieldToCompare: unknown, secondFieldToCompare: unknown): number {
    if (typeof firstFieldToCompare === 'string' && typeof secondFieldToCompare === 'string') {
      return this.sortDirection === 'asc'
        ? firstFieldToCompare.localeCompare(secondFieldToCompare)
        : secondFieldToCompare.localeCompare(firstFieldToCompare);
    }
    if (typeof firstFieldToCompare === 'number' && typeof secondFieldToCompare === 'number') {
      return this.sortDirection === 'asc'
        ? firstFieldToCompare - secondFieldToCompare
        : secondFieldToCompare - firstFieldToCompare;
    }
    if (firstFieldToCompare instanceof Date && secondFieldToCompare instanceof Date) {
      return this.sortDirection === 'asc'
        ? firstFieldToCompare.getTime() - secondFieldToCompare.getTime()
        : secondFieldToCompare.getTime() - firstFieldToCompare.getTime();
    }
    const a = String(firstFieldToCompare);
    const b = String(secondFieldToCompare);
    return this.sortDirection === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  }

  get sortRecordsByKey(): TRow[] {
    if (this.args.data == null) {
      return [];
    }
    // if there is no sort, or a custom sort handler is provided, then we don't sort the data
    if (!this.sortBy || this.args.onSortToggle) {
      return this.args.data;
    }

    return [...this.args.data].sort((firstRecord, secondRecord) => {
      const sortingColumn = this.args.columnHeaders.find((header) => header.key === this.sortBy);
      if (sortingColumn == null || !sortingColumn.sortable || this.sortBy == null) return 0;

      const firstAttribute = this.getRecordValue(firstRecord, this.sortBy);
      const secondAttribute = this.getRecordValue(secondRecord, this.sortBy);

      return this.compareFields(firstAttribute, secondAttribute);
    });
  }

  getComponentArgs(row: TRow, column: ColumnHeader): { template: string | ComponentLike; row: TRow } | undefined {
    if (column.componentOptions == null) {
      return;
    }

    return {
      template: column.componentOptions.template,
      row,
    };
  }

  getRecordValue(record: TRow, key: string): string {
    const value = get(record, key);
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return String(value ?? '');
  }

  @action
  toggleActionMenu(record: TRow): void {
    const recordId = record.id;
    if (this.activeActionMenuId === recordId) {
      this.activeActionMenuId = null;
      this.removeClickOutsideListener();
    } else {
      this.activeActionMenuId = recordId;
      setTimeout(() => this.addClickOutsideListener(), 0);
    }
    this.args.onRowSelect?.(record);
  }

  @action
  closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.removeClickOutsideListener();
    this.args.onClearRowSelection?.();
  }

  @action
  setActionMenuElement(element: HTMLElement | null): void {
    this.actionMenuElement = element;
  }

  @action
  onRowClick(row: TRow): void {
    this.args.onRowClick?.(row);
  }

  addClickOutsideListener(): void {
    document.addEventListener('mousedown', this.handleClickOutside, true);
  }

  removeClickOutsideListener(): void {
    document.removeEventListener('mousedown', this.handleClickOutside, true);
  }

  handleClickOutside = (event: MouseEvent): void => {
    if (this.actionMenuElement && !this.actionMenuElement.contains(event.target as Node)) {
      this.closeActionMenu();
    }
  };

  @action
  onSortToggle(columnKey: string, sortDirection: SortDirection): void {
    this.sortBy = columnKey;
    this.sortDirection = sortDirection;
    this.args.onSortToggle?.(columnKey, sortDirection);
  }

  @action
  onSelectAll(event: Event): void {
    if (this.args.onSelectAll) {
      const target = event.target as HTMLInputElement;
      this.args.onSelectAll(target?.checked);
    }
  }

  <template>
    <div class="flex items-center justify-between" ...attributes>
      <table>
        <thead>
          <tr>
            {{#each @columnHeaders as |header|}}
              <th
                data-test-table-column-header
                data-test-table-header={{header.key}}
                class="align-middle border-b {{@headerClass}} {{header.class}}"
              >
                <SortableHeaderColumn
                  @name={{header.label}}
                  @isActive={{eq this.sortBy header.key}}
                  @sortDirection={{this.sortDirection}}
                  @sortField={{if header.sortable header.key}}
                  @sortAction={{this.onSortToggle}}
                />
              </th>
            {{/each}}
            {{#if (gt @actions.length 0)}}
              <th class="align-middle border-b"></th>
            {{/if}}
          </tr>
        </thead>
        <tbody>
          {{#each this.sortedData as |record|}}
            <tr
              data-test-table-row={{this.getRecordValue record "id"}}
              class="align-middle h-18 border-b border-t {{if @onRowClick 'cursor-pointer'}}"
              {{on "click" (fn this.onRowClick record)}}
            >
              {{#each @columnHeaders as |column|}}
                <td
                  class="align-middle p-4 border-b text-carbon-50 font-normal text-sm {{column.class}}"
                  data-test-table-cell={{column.key}}
                >
                  {{#let (this.getComponentArgs record column) as |componentArgs|}}
                    {{#if componentArgs}}
                      {{#let (ensureSafeComponent componentArgs.template this) as |DynamicComponent|}}
                        {{! @glint-nocheck: not all caller components are converted to GTS }}
                        <DynamicComponent @row={{componentArgs.row}} />
                      {{/let}}
                    {{else}}
                      {{this.getRecordValue record column.key}}
                    {{/if}}
                  {{/let}}
                </td>
              {{/each}}

              {{#if (gt @actions.length 0)}}
                <td class="px-2 align-middle border-b" tabindex="-1" data-test-reservation-feed-cell="menu">
                  {{#if (this.hasActionMenu record)}}
                    <div
                      data-test-action-menu-trigger
                      class="font-bold py-2 text-center text-carbon-checkbox cursor-pointer"
                      {{on "click" (fn this.toggleActionMenu record)}}
                      {{on "blur" this.closeActionMenu}}
                    >
                      ...
                    </div>
                    {{#if (this.showActionMenu record)}}
                      <div
                        data-test-action-menu
                        class="flex flex-col absolute right-0 bg-white cursor-pointer rounded-lg z-10 min-w-[160px] shadow-lg"
                        {{clickOutside this.closeActionMenu}}
                        {{on "click" this.closeActionMenu}}
                      >
                        {{#each @actions as |tableAction index|}}
                          {{#let
                            (if (eq index 0) "rounded-t-lg" (if (eq index this.lastActionIndex) "rounded-b-lg" ""))
                            as |radiusClass|
                          }}
                            <div
                              data-test-action-item
                              {{on "click" (fn tableAction.onAction record)}}
                              class={{concat
                                tableAction.className
                                " flex items-center gap-2 mt px-4 py-2 "
                                radiusClass
                                " "
                                (if
                                  (tableAction.disabled record)
                                  "text-gray-300 opacity-50 cursor-default"
                                  "cursor-pointer hover:bg-gem-5"
                                )
                              }}
                            >
                              {{#if tableAction.icon}}
                                <img
                                  src={{tableAction.icon}}
                                  alt="icon"
                                  class="{{tableAction.iconClass}} w-[16px] h-[16px]"
                                />
                              {{/if}}
                              <span class="leading-[16px] flex items-center">{{tableAction.label}}</span>
                            </div>
                          {{/let}}
                        {{/each}}
                      </div>
                    {{/if}}
                  {{/if}}
                </td>
              {{/if}}
            </tr>
          {{/each}}
        </tbody>
      </table>
    </div>
    {{yield this}}
  </template>
}
